import { defineChain, getAddress, type Address } from 'viem';

import type { Addresses } from '../../offchain/src/config.ts';

export type Deployment = Addresses & { papurr: Address; tokenomics?: Address; usdc?: Address; oracle?: Address };

const files = import.meta.glob<Record<string, string>>('../../deployments/*.json', { eager: true, import: 'default' });

function env(name: string, fallback?: string): string {
  const v = (import.meta.env[name] as string | undefined) || fallback;
  if (v === undefined) throw new Error(`missing ${name} (see .env.example)`);
  return v;
}

function loadDeployment(network: string): Deployment {
  const raw = files[`../../deployments/${network}.json`];
  if (!raw) throw new Error(`deployments/${network}.json not found`);
  const pick = (k: string) => {
    const v = raw[k];
    if (!v) throw new Error(`deployments/${network}.json has no ${k}`);
    return getAddress(v);
  };
  return {
    exchange: pick('exchange'),
    executor: pick('executor'),
    sessionKeyManager: pick('sessionKeyManager'),
    staking: pick('staking'),
    depositFactory: pick('depositFactory'),
    papurr: pick('papurr'),
    tokenomics: raw.tokenomics ? getAddress(raw.tokenomics) : undefined,
    usdc: raw.usdc ? getAddress(raw.usdc) : undefined,
    oracle: raw.oracle ? getAddress(raw.oracle) : undefined,
    hypeBuyback: raw.hypeBuyback ? getAddress(raw.hypeBuyback) : undefined,
    burnAuction: raw.burnAuction ? getAddress(raw.burnAuction) : undefined,
    cctpGateway: raw.cctpGateway ? getAddress(raw.cctpGateway) : undefined,
    hypeConverter: raw.hypeConverter ? getAddress(raw.hypeConverter) : undefined,
  };
}

const chainId = Number(env('VITE_CHAIN_ID', '999'));
const rpcUrl = env('VITE_RPC_URL', 'https://rpc.hyperliquid.xyz/evm');
const explorer = env('VITE_EXPLORER_URL', '');

export const config = {
  appName: env('VITE_APP_NAME', 'Papurrtrade'),
  network: env('VITE_NETWORK', 'mainnet'),
  hlInfoUrl: env('VITE_HL_INFO_URL', 'https://api.hyperliquid.xyz/info'),
  chainId,
  rpcUrl,
  relayerUrl: env('VITE_RELAYER_URL').replace(/\/$/, ''),
  /** Live push from the API; defaults to the relayer URL on ws(s)://.../ws */
  wsUrl: env('VITE_WS_URL', `${env('VITE_RELAYER_URL').replace(/\/$/, '').replace(/^http/, 'ws')}/ws`),
  sessionDays: Number(env('VITE_SESSION_DAYS', '7')),
  /** Max leverage named in the docs (the order ticket reads the live one from the market): 500 on mainnet */
  maxLeverage: Number(env('VITE_MAX_LEVERAGE', '500')),
  /** Per-position margin cap and notional cap the docs name, USD; the order ticket reads the live ones */
  maxMarginUsd: Number(env('VITE_MAX_MARGIN_USD', '300000')),
  maxNotionalUsd: Number(env('VITE_MAX_NOTIONAL_USD', '1000000')),
  /** Minimum margin per position the docs name, USD */
  minMarginUsd: Number(env('VITE_MIN_MARGIN_USD', '10')),
  /** The keeper's open interest caps the docs name, USD: the floor per side and the hard cap (CAP_POLICY offchain) */
  oiMinCapUsd: Number(env('VITE_OI_MIN_CAP_USD', '150000')),
  oiHardCapUsd: Number(env('VITE_OI_HARD_CAP_USD', '3000000')),
  /** Smallest balance an account may keep staked, PAPURR (0: none) */
  minStake: Number(env('VITE_MIN_STAKE', '1000')),
  /** PAPURR revenue's share of a loss named in the docs, percent: 4 on mainnet (the canary was deployed with 5); the buyback splits it between stakers and the burn auction */
  stakerLossPct: Number(env('VITE_STAKER_LOSS_PCT', '4')),
  /** Win fee named in the docs, percent: 2 on mainnet (the canary was deployed with 3) */
  winFeePct: Number(env('VITE_WIN_FEE_PCT', '2')),
  /** The mint curve named in the docs, millions of USD: flat part and decay constant (mainnet 0.5 and 8, deployed with 60; canary 2 and 120) */
  mintFlatM: Number(env('VITE_MINT_FLAT_M', '0.5')),
  mintTailKM: Number(env('VITE_MINT_TAIL_K_M', '8')),
  /** The low-house multiple named in the docs: the curve's rate x this at an empty house (mainnet 2.5; 0 names a tokenomics without the ramp, the canary) */
  mintLowHouseX: Number(env('VITE_MINT_LOW_HOUSE_X', '2.5')),
  /** The burn auction's share of every HYPE lot named in the docs, percent: 50 on mainnet (deployed with 30), 10 on the canary */
  auctionPct: Number(env('VITE_AUCTION_PCT', '50')),
  /** House liquidity above which the PAPURR revenue takes a share of its gains, millions of USD: 0.75 on mainnet, 5 on the canary */
  rewardThresholdM: Number(env('VITE_REWARD_THRESHOLD_M', '0.75')),
  /** PAPURR revenue's share of each new house record named in the docs, percent: 30 at launch (setRewardShares may raise it) */
  newHighPct: Number(env('VITE_NEW_HIGH_PCT', '30')),
  /**
   * Preview only: the leverage the ticket offers instead of the market's, to show a coming limit (a demo build);
   * orders above the market's real limit stay blocked. Unset in every real deployment.
   */
  previewMaxLeverage: Number(env('VITE_PREVIEW_MAX_LEVERAGE', '0')),
  /** A notice shown under the header until dismissed (a beta or canary warning); empty: none */
  betaNotice: env('VITE_BETA_NOTICE', ''),
  explorer,
  /** anvil: dev accounts are unlocked, so the app can sign without a wallet extension */
  isLocal: chainId === 31337,
  chain: defineChain({
    id: chainId,
    name: chainId === 999 ? 'HyperEVM' : chainId === 31337 ? 'Anvil' : `Chain ${chainId}`,
    nativeCurrency: { name: 'HYPE', symbol: 'HYPE', decimals: 18 },
    rpcUrls: { default: { http: [rpcUrl] } },
    blockExplorers: explorer ? { default: { name: 'Explorer', url: explorer } } : undefined,
  }),
};

export const deployment = loadDeployment(config.network);

/** Display metadata; the risk parameters come from the exchange. One market: HYPE. */
/**
 * Markets by exchange instrument id. `hlCoin` is Hyperliquid's name for the chart and the 24h stats: a
 * validator-operated perp by its name (`BTC`), a HIP-3 perp with its dex (`xyz:SP500`), a spot pair by its index
 * (`@107`); the instrument's onchain asset id must be the same market (see HyperCore.referencePx).
 */
export const MARKETS: Record<number, { symbol: string; name: string; hlCoin: string; color: string }> = {
  0: { symbol: 'HYPE', name: 'Hyperliquid', hlCoin: 'HYPE', color: '#50e3c2' },
};

export const marketBySymbol = (symbol: string) =>
  Number(Object.entries(MARKETS).find(([, m]) => m.symbol.toLowerCase() === symbol.toLowerCase())?.[0] ?? 0);

export const txUrl = (hash: string) => (explorer ? `${explorer}/tx/${hash}` : undefined);
